import {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from "react";
import { ShellContext } from "./ShellContext";
import type { ShellId, ShellAccess } from "./types";

const SHELL_STORAGE_KEY = "sentinels_active_shell";

/** Default access when session is not yet loaded. */
const DEFAULT_SHELL_ACCESS: ShellAccess = {
  global: false,
  ops: false,
  client: false,
  field: false,
};

/** Resolve the default shell from access flags. Priority: core/global > ops > client. */
function resolveDefaultShell(access: ShellAccess): ShellId {
  if (access.global) return "admin";
  if (access.ops) return "ops";
  if (access.client) return "client";
  // Fallback — should not happen if session is valid
  return "ops";
}

/** Check if a shell ID is valid for the given access. */
function isShellAllowed(shell: ShellId, access: ShellAccess): boolean {
  switch (shell) {
    case "admin":
      return access.global;
    case "ops":
      return access.ops;
    case "client":
      return access.client;
  }
}

function getAvailableShells(access: ShellAccess): ShellId[] {
  const shells: ShellId[] = [];
  if (access.global) shells.push("admin");
  if (access.ops) shells.push("ops");
  if (access.client) shells.push("client");
  return shells;
}

interface ShellProviderProps {
  children: ReactNode;
  shellAccess?: ShellAccess;
}

export function ShellProvider({
  children,
  shellAccess = DEFAULT_SHELL_ACCESS,
}: ShellProviderProps) {
  const isResolved =
    shellAccess.global || shellAccess.ops || shellAccess.client;

  const [activeShell, setActiveShellState] = useState<ShellId>(() => {
    // Try to restore from localStorage. We trust the stored value here even
    // when shellAccess is still the empty default — the post-bootstrap effect
    // below will reject it if it turns out the user has no access.
    const stored = localStorage.getItem(SHELL_STORAGE_KEY) as ShellId | null;
    if (stored) return stored;
    return resolveDefaultShell(shellAccess);
  });

  // Track whether the active shell is the result of an explicit user choice
  // (`setActiveShell`). When the user has explicitly chosen a shell we never
  // override it on bootstrap; otherwise we re-resolve to the highest-priority
  // shell once shellAccess is hydrated. This fixes the platform_admin →
  // /ops/dashboard regression (see issue #357).
  const explicitChoiceRef = useRef<boolean>(
    typeof window !== "undefined"
      ? localStorage.getItem(SHELL_STORAGE_KEY) !== null
      : false,
  );

  const setActiveShell = useCallback(
    (shell: ShellId) => {
      if (!isShellAllowed(shell, shellAccess)) return;
      localStorage.setItem(SHELL_STORAGE_KEY, shell);
      explicitChoiceRef.current = true;
      setActiveShellState(shell);
    },
    [shellAccess],
  );

  // Once bootstrap completes (isResolved transitions to true), re-sync the
  // active shell against the now-known shellAccess. We honour an explicit
  // user choice if it is still allowed; otherwise we fall back to the
  // highest-priority default. Without this, a platform_admin user who lands
  // on the SPA before bootstrap finishes gets locked into "ops" forever
  // because the initial render saw shellAccess.global=false and picked "ops".
  useEffect(() => {
    if (!isResolved) return;

    const stored = localStorage.getItem(SHELL_STORAGE_KEY) as ShellId | null;
    if (
      explicitChoiceRef.current &&
      stored &&
      isShellAllowed(stored, shellAccess)
    ) {
      if (stored !== activeShell) {
        setActiveShellState(stored);
      }
      return;
    }

    // No explicit choice (or it is no longer allowed) → resolve to the
    // highest-priority shell the user actually has access to.
    const def = resolveDefaultShell(shellAccess);
    if (def !== activeShell) {
      setActiveShellState(def);
    }
  }, [isResolved, shellAccess, activeShell]);

  // Re-resolve when shellAccess changes (e.g. after bootstrap loads). This
  // is a render-time guard so the consumer never sees an inactive shell;
  // the effect above handles the long-term reconciliation.
  const resolvedShell = useMemo(() => {
    if (isShellAllowed(activeShell, shellAccess)) return activeShell;
    return resolveDefaultShell(shellAccess);
  }, [activeShell, shellAccess]);

  const availableShells = useMemo(
    () => getAvailableShells(shellAccess),
    [shellAccess],
  );

  const value = useMemo(
    () => ({
      activeShell: resolvedShell,
      shellAccess,
      availableShells,
      setActiveShell,
      isResolved,
    }),
    [resolvedShell, shellAccess, availableShells, setActiveShell, isResolved],
  );

  return (
    <ShellContext value={value}>
      <div data-shell={resolvedShell} className="contents">
        {children}
      </div>
    </ShellContext>
  );
}
